/**
 * CodeChangeWarning - Aviso permanente de cambio de código al guardar
 *
 * @version 1.0
 * @updated 2026-02-18
 * @module core/coding
 *
 * @description
 * Muestra un aviso inline cuando mover un objeto a otra celda de la malla
 * canónica provocará que su código cambie al guardar.
 *
 * @remarks
 * Filosofía "El código es una dirección": el aviso es informativo, no bloqueante.
 * El usuario puede guardar igualmente; el cambio de código se registrará
 * automáticamente en el historial de objetos.
 *
 * Solo debe renderizarse cuando willRecode===true.
 */

import './coding.css';

/**
 * Props del componente
 */
export interface CodeChangeWarningProps {
  /**
   * Código actual del objeto (antes del movimiento)
   */
  currentCode: string;
  /**
   * Código tentativo que tendrá el objeto al guardar.
   * Puede ser null si aún se está calculando o si el backend no pudo determinarlo.
   */
  tentativeNewCode: string | null;
}

/**
 * Aviso permanente de cambio de código por movimiento del objeto
 *
 * @example
 * ```tsx
 * {recodeStatus?.willRecode && (
 *   <CodeChangeWarning
 *     currentCode={recodeStatus.currentCode}
 *     tentativeNewCode={recodeStatus.tentativeNewCode}
 *   />
 * )}
 * ```
 */
export function CodeChangeWarning({
  currentCode,
  tentativeNewCode,
}: CodeChangeWarningProps): JSX.Element {
  return (
    <div className="code-change-warning" role="status" aria-live="polite">
      <span className="code-change-warning__icon" aria-hidden="true">⚠</span>
      <div className="code-change-warning__body">
        <span className="code-change-warning__title">
          El código cambiará al guardar
        </span>
        <div className="code-change-warning__codes">
          <span
            className="code-change-warning__code code-change-warning__code--current"
            title="Código actual"
          >
            {currentCode}
          </span>
          <span className="code-change-warning__arrow">→</span>
          {tentativeNewCode ? (
            <span
              className="code-change-warning__code code-change-warning__code--new"
              title="Nuevo código (orientativo)"
            >
              {tentativeNewCode}
            </span>
          ) : (
            <span
              className="code-change-warning__code code-change-warning__code--new"
              title="Código a determinar"
            >
              ···
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
